import React, { useEffect, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import ExportedImage from 'next-image-export-optimizer';
import Link from 'next/link';

import kwesforms from 'kwesforms';

import { AccordionItem } from '@/components/shared/lp/AccordionItem';
import BgEllipse from '@/components/shared/lp/BgEllipse';

import { siteData } from '@/data/site';
import mapBg from '@/public/images/lp/contact/map-bg.svg';
import DiscordIcon from '@/public/images/lp/icons/social/Discord_Icon.svg';
import EmailIcon from '@/public/images/lp/icons/social/Email_Icon.svg';
import GithubIcon from '@/public/images/lp/icons/social/Github_Icon.svg';
import RedditIcon from '@/public/images/lp/icons/social/Reddit_Icon.svg';
import TelegramIcon from '@/public/images/lp/icons/social/Telegram_Icon.svg';
import TwitterIcon from '@/public/images/lp/icons/social/Twitter_Icon.svg';
import YoutubeIcon from '@/public/images/lp/icons/social/Youtube_Icon.svg';

export default function Contact() {
  const [selected, setSelected] = useState('contactFaq1');
  useEffect(() => {
    kwesforms.init();
  }, []);

  const socialLinks = [
    {
      href: `mailto:${siteData.defaultEmail}`,
      src: EmailIcon,
      width: 32,
      height: 33,
      alt: 'Email_Icon',
    },
    {
      href: siteData.social.discord,
      src: DiscordIcon,
      width: 32,
      height: 32,
      alt: 'Discord_Icon',
    },
    {
      href: siteData.social.github,
      src: GithubIcon,
      width: 32,
      height: 30,
      alt: 'Github_Icon',
    },
    {
      href: siteData.social.twitter,
      src: TwitterIcon,
      width: 32,
      height: 32,
      alt: 'Twitter_Icon',
    },
    {
      href: siteData.social.youtube,
      src: YoutubeIcon,
      width: 33,
      height: 33,
      alt: 'Youtube_Icon',
    },
    {
      href: siteData.social.telegram,
      src: TelegramIcon,
      width: 32,
      height: 33,
      alt: 'Telegram_Icon',
    },
    {
      href: siteData.social.reddit,
      src: RedditIcon,
      width: 33,
      height: 33,
      alt: 'Reddit_Icon',
    },
  ];

  const faqData = [
    {
      itemId: 'contactFaq1',
      parentAccId: 'contactFaqAcc',
      itemBodyId: 'contactFaqBody1',
      heading: 'What is Komodo?',
      body: (
        <span className='space-y-4'>
          <p>
            Komodo can best be described as an open-source technology workshop that focuses
            specifically on advancing freedom through blockchain innovation.
          </p>
          <p>
            Komodo Wallet is the flagship end-user application that has emerged from the Komodo
            Workshop. Several robust open-source technologies have been produced in the Komodo
            Workshop, including the Komodo DeFi Framework, delayed Proof of Work (dPoW), UTXO smart
            contracts (CC modules), nSPV, and more.
          </p>
        </span>
      ),
    },
    {
      itemId: 'contactFaq2',
      parentAccId: 'contactFaqAcc',
      itemBodyId: 'contactFaqBody2',
      heading: <>I&apos;m a developer. What can I build with Komodo technology?</>,
      body: (
        <span className='space-y-4'>
          <p>
            Komodo&apos;s open-source technologies enable developers to create cross-protocol DeFi
            applications such as multi-coin wallets, DEXs, and custom Initial DEX Offering (IDO)
            portals. We also provide the technical infrastructure for anyone to launch sovereign
            blockchains with no programming required. Check out our&nbsp;
            <Link
              href='https://github.com/KomodoPlatform/'
              target='_blank'
              rel='noopener noreferrer'
            >
              GitHub &nbsp;
            </Link>
            and &nbsp;
            <Link href='https://komodoplatform.com/en/docs/'>developer documentation</Link>
            to learn more.
          </p>
        </span>
      ),
    },
    {
      itemId: 'contactFaq3',
      parentAccId: 'contactFaqAcc',
      itemBodyId: 'contactFaqBody3',
      heading: 'I want to join the Komodo community. How do I learn more and get involved?',
      body: (
        <span className='space-y-4'>
          <p>
            The best way to get started is to read the
            <Link
              href='https://komodoplatform.com/en/blog/komodo-handbook/'
              target='_blank'
              rel='noopener noreferrer'
            >
              Komodo Handbook
            </Link>
            - our complete guide to learning everything about Komodo&apos;s products & technologies,
            history, and community.
          </p>
        </span>
      ),
    },
    {
      itemId: 'contactFaq4',
      parentAccId: 'contactFaqAcc',
      itemBodyId: 'contactFaqBody4',
      heading: <>I&apos;m a business. How can I partner with Komodo?</>,
      body: (
        <span className='space-y-4'>
          <p>
            Visit our
            <Link href='/en/business.html' target='_blank' rel='noopener noreferrer'>
              business page
            </Link>
            to learn more about Komodo&apos;s end-to-end blockchain consulting and development. Fill
            out the contact form to reach out to our business team regarding partnership
            opportunities.
          </p>
        </span>
      ),
    },
    {
      itemId: 'contactFaq5',
      parentAccId: 'contactFaqAcc',
      itemBodyId: 'contactFaqBody5',
      heading: 'What is KMD?',
      body: (
        <span className='space-y-4'>
          <p>
            KMD is the community currency for the Komodo ecosystem and is used for scalable, fast,
            cheap, and secure transactions. Looking for a KMD wallet? The
            <Link href='/en/wallet/' target='_blank' rel='noopener noreferrer'>
              wallets page
            </Link>
            shows a list of KMD wallets.
          </p>
        </span>
      ),
    },
  ];

  return (
    <>
      {/* <div className='absolute z-[11] h-full w-full overflow-hidden opacity-70'>
        <div className='index-ellipse-1 absolute z-[-10]'></div>
      </div> */}
      <div className='absolute top-[150px] z-[11] h-[100vh] w-full overflow-hidden opacity-70'></div>
      <main
        className={`container relative z-[100] mx-auto font-sans text-white md:grid md:grid-cols-24 md:gap-0`}
      >
        <ExportedImage
          src={mapBg}
          alt='mapBg'
          className='absolute left-1/2 top-[150px] z-[-1] h-[100vh] w-auto -translate-x-1/2 transform'
        />
        <div className='lp-ellipse-223 absolute right-[0px] top-[-300px] z-[-10] h-[585px] w-[560px]'></div>
        <div className='lp-ellipse-224 absolute left-[-100px] top-[163px] z-[-10] h-[515px] w-[492px]'></div>
        <div className='lp-ellipse-232 absolute right-[-200px] top-[800px] z-[-10] h-[733px] w-[700px]'></div>
        <div className='lp-ellipse-233 absolute left-[-400px] top-[1700px] z-[-10] h-[783px] w-[749px]'></div>

        <div className='hidden xl:col-span-2 xl:block'></div>
        <div className='relative w-full max-w-7xl px-4 xs:px-8 md:col-span-24 md:mx-auto xl:col-span-20 xl:px-0'>
          <section className='mx-auto flex flex-col items-center px-5 pt-[80px] sm:pt-[100px] 3xl:pt-[150px]'>
            <h1 className='mb-[50px] mt-1 flex items-center text-center font-heading text-[73px] font-semibold leading-[120%] tracking-[-2.19px] text-white hover:text-gray-300 sm:mb-[50px] lg:block 2xl:mb-[100px] -sm:text-[40px]'>
              <span className='trustless-gradient mr-2'>Contact</span> Us
            </h1>
            <div className='relative grid w-full grid-cols-1 gap-10 lg:grid-cols-2'>
              <div className='hero-contact overflow-hidden bg-gradient-to-br from-[rgb(51,86,123,1)] to-[rgb(17,114,217,1)] p-5 py-10'>
                <form
                  action='https://kwes.io/api/foreign/forms/FjzAcC7ZHTCxNjF1LfTY'
                  className='kwes-form contact-form space-y-5'
                  has-recaptcha-v2='true'
                  no-reload='true'
                >
                  <fieldset className=''>
                    <input
                      type='email'
                      // value=''
                      name='EMAIL'
                      className='block w-full border-2 border-[#0e959e] bg-[#0d3b55] px-5 py-3 text-lg font-normal leading-7 text-white placeholder:text-white focus:outline-none disabled:bg-[#f1f4f8]'
                      placeholder='Email (required)'
                      rules='required|email|max:255'
                    />
                    <span className='form-control-line'></span>
                  </fieldset>
                  <div className='form-group'>
                    <select
                      name='Topic'
                      className='w-full rounded-none border-2 border-[#0e959e] bg-[#0d3b55] px-5 py-3.5 text-white shadow-inner -2xs:max-w-[238px]'
                      rules='required'
                    >
                      <option placeholder='true' value=''>
                        Choose a Topic (required)
                      </option>
                      <option value='Komodo Wallet Integration'>Komodo Wallet Integration</option>
                      <option value='Blockchain 51% Security'>Blockchain 51% Security</option>
                      <option value='Blockchain Creation'>Blockchain Creation</option>
                      <option value='Grants'>Grants</option>
                      <option value='Partnerships'>Partnerships</option>
                      <option value='Support Request'>Support Request</option>
                      <option value='Other'>Other</option>
                    </select>
                    <span className='form-control-line'></span>
                  </div>
                  <div className='form-group'>
                    <textarea
                      name='Text'
                      className='required form-control min-h-[340px] w-full resize-none border-2 border-[#0e959e] bg-[#0d3b55] px-5 py-3.5 leading-7 text-white shadow-inner'
                      placeholder='Your message (required)'
                      rules='required'
                    ></textarea>
                    <span className='form-control-line'></span>
                  </div>
                  <div className='clear text-right'>
                    <ReCAPTCHA
                      sitekey='6LcMeu0iAAAAAPSzR-vtTQkxULQXQZ5yUqA-VtwP'
                      className='mb-4'
                    />
                    <input
                      type='submit'
                      value='send'
                      name='send'
                      className='cta-btn flex h-14 min-w-[150px] items-center justify-center rounded-[14px] px-7 py-2 text-base font-bold uppercase text-white'
                    />
                  </div>
                </form>
              </div>
              <div className='flex justify-center lg:items-start lg:justify-end'>
                <div className='hero-contact-info mt-lg-0 ml-lg-6 ml-xl-0 w-max space-y-5 bg-gradient-to-br from-[rgb(51,86,123,1)] to-[rgb(17,114,217,1)] p-4 xs:p-10'>
                  <h2 className='text-[1.5em]'>We Are Always Online</h2>
                  <div className='space-y-1'>
                    <h5 className='text-[#12fefe]'>Email:</h5>
                    <p className='text-[1.3125rem]'>{siteData.defaultEmail}</p>
                  </div>
                  <ul className='flex gap-4'>
                    {socialLinks.map((link, index) => (
                      <li key={index}>
                        <Link href={link.href} target='_blank' rel='noopener noreferrer'>
                          <ExportedImage
                            src={link.src}
                            width={link.width}
                            height={link.height}
                            alt={link.alt}
                            className='h-10 w-10'
                          />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>
          <section className='faq-fold section py-20 text-white lg:py-32'>
            <div className='relative z-10 mx-auto max-w-[1300px] space-y-10 px-5'>
              <h2 className='text-center text-[2em] md:text-[2.25em]'>
                Frequently Asked Questions
              </h2>
              <div className='mx-auto max-w-[800px]'>
                <div className='space-y-10' id='contactFaqAcc'>
                  {faqData.map((item, index) => (
                    <AccordionItem
                      key={index}
                      itemId={item.itemId}
                      parentAccId={item.parentAccId}
                      itemBodyId={item.itemBodyId}
                      heading={item.heading}
                      body={item.body}
                      selected={selected}
                      setSelected={setSelected}
                    />
                  ))}
                </div>
              </div>
              <div className='flex flex-col items-center justify-center gap-4 py-10 text-center'>
                <h4 className='text-xl'>Still Have Questions?</h4>
                <p className=''>Join our Discord and ask on one of our public chat rooms</p>
                <Link
                  href={siteData.social.discord}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='flex h-14 w-[150px] items-center justify-center rounded-md bg-[linear-gradient(90deg,_#5a68e6_12.55%,_#4986ea_93.3%)] px-7 py-2 text-base font-bold uppercase text-white'
                >
                  Ask Us
                </Link>
              </div>
            </div>
          </section>
        </div>
        <div className='hidden xl:col-span-2 xl:block'></div>
      </main>
    </>
  );
}
